
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RefreshCw, PhoneCall, FileText, LayoutList } from "lucide-react";
import ConversationDataCard from "./ConversationDataCard";
import ConversationAudioPlayer from "./ConversationAudioPlayer";
import ConversationTranscriptView from "./ConversationTranscriptView";
import { truncateId } from "./conversationUtils";

interface ConversationTabsViewProps {
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  conversationDetails: any;
  conversationId: string;
  audioData: any;
  isLoadingAudio: boolean;
  handleFetchAudio: () => void;
}

const ConversationTabsView = ({
  currentTab,
  setCurrentTab,
  conversationDetails,
  conversationId,
  audioData,
  isLoadingAudio,
  handleFetchAudio
}: ConversationTabsViewProps) => {
  return (
    <div className="p-4">
      <Tabs value={currentTab} onValueChange={setCurrentTab} className="w-full">
        <TabsList className="w-full grid grid-cols-3 mb-2">
          <TabsTrigger value="details" className="flex items-center gap-1.5">
            <LayoutList className="h-4 w-4" />
            <span>Details</span>
          </TabsTrigger>
          <TabsTrigger value="audio" className="flex items-center gap-1.5">
            <PhoneCall className="h-4 w-4" />
            <span>Audio</span>
          </TabsTrigger>
          <TabsTrigger value="transcript" className="flex items-center gap-1.5">
            <FileText className="h-4 w-4" />
            <span>Transcript</span>
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="details" className="pt-2 focus-visible:outline-none focus-visible:ring-0 focus:outline-none">
          <ConversationDataCard conversationData={conversationDetails} />
        </TabsContent>
        
        <TabsContent value="audio" className="pt-2 focus-visible:outline-none focus-visible:ring-0 focus:outline-none">
          {audioData ? (
            <ConversationAudioPlayer 
              audioData={audioData}
              conversationId={conversationId}
              truncateId={truncateId}
            />
          ) : (
            <div className="flex flex-col items-center space-y-4 py-8 bg-accent/10 rounded-lg border border-dashed">
              <div className="text-center max-w-md px-4">
                <PhoneCall className="h-10 w-10 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No Audio Available</h3>
                <p className="text-muted-foreground mb-6">
                  The audio recording for this conversation hasn't been fetched yet or isn't available.
                </p>
                <Button 
                  onClick={handleFetchAudio} 
                  disabled={isLoadingAudio}
                  size="lg"
                >
                  {isLoadingAudio ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Loading Audio...
                    </>
                  ) : (
                    <>
                      Fetch Audio Recording
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="transcript" className="pt-2 focus-visible:outline-none focus-visible:ring-0 focus:outline-none">
          <ConversationTranscriptView conversationDetails={conversationDetails} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ConversationTabsView;
